import { BasicFetchResult } from "/@/api/model/baseModel";
import { PlatformType } from "/@/hooks/usePlatform";
import { defHttp } from "/@/utils/http/axios";
import { IPage } from "/@/views/pages/types";
// ========== 健康状态相关接口 ==========
import { HealthStatus, EuPrivacyResult, ThrottleScope } from "/@/views/pages/types";

enum Api {
  basicPath = "/pages",
}

/**
 * 删除指定绑定专页
 * confirm 要拼进 url，走 params 会被 beforeRequestHook 转成 body，后端读不到
 * errorMessageMode: 'none' 让调用方接管 400 文案（模板专页那段长文案要弹确认框，不能塞 toast）
 * @param confirm 模板专页二次确认后传 true
 */
export function deletePage(pageId: string, confirm?: boolean) {
  return defHttp.delete(
    {
      url: `${Api.basicPath}/${pageId}${confirm ? "?confirm=1" : ""}`,
    },
    { errorMessageMode: "none" }
  );
}

/**
 * 获取当前账号下所有facebook pages
 */
export function getPageSubscribe(platform: PlatformType) {
  return defHttp.get<IPage[]>({
    url: `${Api.basicPath}/subscribe`,
    params: {
      platform: platform,
    },
  });
}

/**
 * 获取专页列表
 * @param params
 */
export function getPagesList(params) {
  return defHttp.get<BasicFetchResult<IPage>>({
    url: Api.basicPath,
    params,
  });
}

/**
 * 获取专页信息
 * @param id 专页记录的 _id（ObjectId），不是 page_id —— 传 page_id 后端返回 400
 */
export function getPageInfo(id: string) {
  return defHttp.get<IPage>({
    url: `${Api.basicPath}/${id}/edit`,
  });
}

/**
 * 后端要求提交 is_template 时必须同时携带 note
 */
export function updatePage(pageId: string, payload: { note: string; is_template?: boolean }) {
  return defHttp.patch<IPage>({
    url: `${Api.basicPath}/${pageId}`,
    params: payload,
  });
}

/**
 * 添加专页用户
 * @param pageId
 * @param userId
 */
export function addUserToPage(pageId: string, userId: string) {
  return defHttp.put({
    url: `${Api.basicPath}/${pageId}/addUser`,
    params: {
      user_id: userId,
    },
  });
}

/**
 * 移出专页用户
 * @param pageId
 * @param userId
 */
export function removeUserFromPage(pageId: string, userId: string) {
  return defHttp.put({
    url: `${Api.basicPath}/${pageId}/removeUser`,
    params: {
      user_id: userId,
    },
  });
}

/**
 * 订阅专页
 * @param pageId
 */
export function connectPage(pageId: string) {
  return defHttp.post({
    url: `${Api.basicPath}/connect`,
    params: {
      page_id: pageId,
    },
  });
}

/**
 * 取消订阅专页
 * @param pageId
 */
export function disconnectPage(pageId: string) {
  return defHttp.post({
    url: `${Api.basicPath}/disconnect`,
    params: {
      page_id: pageId,
    },
  });
}

export function getPageHealth(pageId: string): Promise<HealthStatus> {
  return defHttp.get<HealthStatus>({ url: `${Api.basicPath}/${pageId}/health` });
}

/**
 * 切换专页自动降速开关 / 限速范围
 * enabled 与 throttle_scope 至少传一个（后端要求），二者可分别独立切换
 * @param params { enabled?: 自动降速开关; throttle_scope?: 限速范围 }
 */
export function togglePageHealth(
  pageId: string,
  params: { enabled?: boolean; throttle_scope?: ThrottleScope }
): Promise<HealthStatus> {
  return defHttp.post<HealthStatus>({
    url: `${Api.basicPath}/${pageId}/health/toggle`,
    params,
  });
}

export function resetPageHealth(pageId: string, type?: "all"): Promise<HealthStatus> {
  return defHttp.post<HealthStatus>({
    url: `${Api.basicPath}/${pageId}/health/reset`,
    params: type ? { type } : undefined,
  });
}

/**
 * 检测欧盟隐私规则影响
 * @param pageId
 */
export function checkEuPrivacy(pageId: string) {
  return defHttp.get<EuPrivacyResult>({
    url: `${Api.basicPath}/${pageId}/eu-privacy-rules`,
  });
}
